// Entrada na interface quando o Ateliê exige login (na nuvem). Com o Dock ligado, o e-mail e a senha
// são os do Dock; sem ele, senha própria e a página do convite, onde a pessoa escolhe a senha.
import React, { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { ApiError, loggedIn, rawRequest } from "../lib";
import { Button } from "../ui";

function Shell({ title, sub, children }: { title: string; sub?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="brand auth-brand"><span className="brand-dot" />ATELIÊ</div>
        <h1 className="auth-title">{title}</h1>
        {sub && <div className="auth-sub">{sub}</div>}
        {children}
      </div>
    </div>
  );
}

export function LoginPage() {
  const [email, setEmail] = useState(() => localStorage.getItem("atelie:email") ?? "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [via, setVia] = useState<{ via: string | null; dock: string | null } | null>(null);
  useEffect(() => {
    rawRequest("GET", "/api/login").then(setVia, () => setVia({ via: "senha", dock: null }));
  }, []);
  const dock = via?.via === "dock";
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await rawRequest("POST", "/api/login", { email, password });
      localStorage.setItem("atelie:email", email.trim());
      loggedIn(r.user);
    } catch (err: any) {
      setError(err.message);
      setPassword("");
    } finally {
      setBusy(false);
    }
  };
  if (!via) return <Shell title="Entrar"><div className="skeleton" style={{ height: 180 }} /></Shell>;
  return (
    <Shell title="Entrar" sub={dock ? "Use o e-mail e a senha do Dock. Quem é da PQG entra direto." : "Prompts, casos de teste e o usage deste Ateliê só aparecem para quem tem acesso."}>
      <form className="auth-form" onSubmit={submit}>
        <label className="field-label">E-mail<input className="input" type="email" autoComplete="username" autoFocus={!email} value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        <label className="field-label">{dock ? "Senha do Dock" : "Senha"}<input className="input" type="password" autoComplete="current-password" autoFocus={!!email} value={password} onChange={(e) => setPassword(e.target.value)} /></label>
        {error && <div className="auth-error">{error}</div>}
        <Button variant="primary" type="submit" loading={busy} disabled={!email.trim() || !password}>{dock ? "Entrar com o Dock" : "Entrar"}</Button>
      </form>
      <div className="auth-foot">
        {dock ? (
          <>Esqueceu a senha? Troque no {via.dock ? <a href={via.dock} target="_blank" rel="noreferrer">Dock</a> : "Dock"}.</>
        ) : (
          "Sem acesso? Peça um convite a quem administra o Ateliê."
        )}
      </div>
    </Shell>
  );
}

export function InvitePage({ token }: { token: string }) {
  const [info, setInfo] = useState<{ email: string; reset: boolean } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, nav] = useLocation();
  useEffect(() => {
    rawRequest("GET", "/api/invites/" + encodeURIComponent(token)).then(setInfo, (e: ApiError) => setLoadError(e.message));
  }, [token]);
  const tooShort = password.length > 0 && password.length < 10;
  const mismatch = again.length > 0 && again !== password;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await rawRequest("POST", "/api/invites/" + encodeURIComponent(token), { password, name });
      loggedIn(r.user);
      nav("/", { replace: true });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  if (loadError) return <Shell title="Convite indisponível" sub={loadError}><a className="btn btn-sm" href="/">Ir para a entrada</a></Shell>;
  if (!info) return <Shell title="Convite"><div className="skeleton" style={{ height: 120 }} /></Shell>;
  return (
    <Shell title={info.reset ? "Trocar a senha" : "Criar seu acesso"} sub={<>Para <b>{info.email}</b>. Este link vale uma vez só.</>}>
      <form className="auth-form" onSubmit={submit}>
        {!info.reset && <label className="field-label">Nome<input className="input" autoComplete="name" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Como aparece para o time" /></label>}
        <input type="email" autoComplete="username" value={info.email} readOnly hidden />
        <label className="field-label">Senha<input className="input" type="password" autoComplete="new-password" autoFocus={info.reset} value={password} onChange={(e) => setPassword(e.target.value)} /></label>
        <label className="field-label">Repita a senha<input className="input" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} /></label>
        <div className={tooShort || mismatch ? "auth-error" : "auth-hint"}>{mismatch ? "As senhas não são iguais." : "Pelo menos 10 caracteres."}</div>
        {error && <div className="auth-error">{error}</div>}
        <Button variant="primary" type="submit" loading={busy} disabled={password.length < 10 || password !== again}>{info.reset ? "Trocar a senha e entrar" : "Criar acesso e entrar"}</Button>
      </form>
    </Shell>
  );
}
